import React, { useCallback, useImperativeHandle, useMemo, useState } from 'react';

import { Box, Collapse, IconButton, SvgIcon, Typography } from '@mui/material';

import IconCopy from 'app/ui-kit/icons/Copy.svg';
import ProtectedData, { isProtectedData, LockIcon } from 'pages/item/v2/components/ProtectedData';
import isEmpty from 'utils/is-empty';

import useStyles from './styles';

import { COLORS, NAMES_MAP } from '../../constants';

interface Props {
    score: string | number;
    severity: string;
    name: string;
    vector?: string;
    vectorItems?: Record<string, React.ReactNode>;
    defaultIsOpen?: boolean;
}

export interface ScorePaneRef {
    setIsOpen: (isOpen: boolean) => void;
}

const ScorePane: React.ForwardRefRenderFunction<ScorePaneRef, Props> = (
    { score, name, vector, vectorItems, severity, defaultIsOpen = false },
    ref
) => {
    const { classes } = useStyles();
    const [isOpen, setIsOpen] = useState(defaultIsOpen);

    const toggleOpen = useCallback(() => setIsOpen((state) => !state), []);
    useImperativeHandle(ref, () => ({ setIsOpen }), []);

    const hasVectorItems = useMemo(() => !!vectorItems && isEmpty(vectorItems) === false, [vectorItems]);

    return (
        <div key={name} className={classes.container}>
            <Box
                className={classes.pane}
                onClick={toggleOpen}
                style={{
                    background: COLORS.BACKGROUND[severity],
                    cursor: hasVectorItems ? 'pointer' : 'initial',
                    borderBottomLeftRadius: hasVectorItems && isOpen ? 0 : 18,
                    borderBottomRightRadius: hasVectorItems && isOpen ? 0 : 18
                }}
            >
                <Box display="flex" flexDirection="row" flex={1}>
                    <div className={classes.accent} style={{ background: COLORS.ACCENT[severity] }} />

                    <Typography variant="body1">
                        <ProtectedData data={score}>{({ content }) => `${content} ${severity}`}</ProtectedData>
                    </Typography>
                </Box>
                <Typography variant="body2" display="flex" alignItems="center" gap={'4px'}>
                    {isProtectedData(score) ? <LockIcon /> : null}
                    {name}
                </Typography>
            </Box>
            {hasVectorItems ? (
                <Collapse in={isOpen}>
                    <div>
                        {Object.entries(vectorItems as object).map(([key, value]) => (
                            <Box key={`${name}-${key}`} className={classes.vectorItem}>
                                <Typography variant="body2" display="flex" alignItems="center" gap={'4px'}>
                                    {NAMES_MAP[key]}
                                    {isProtectedData(score) ? <LockIcon /> : null}
                                </Typography>
                                <Typography variant="body2">{<ProtectedData data={value} />}</Typography>
                            </Box>
                        ))}
                    </div>

                    {vector ? (
                        <Box className={classes.vector}>
                            <ProtectedData data={vector}>
                                {({ isProtected, content }) => (
                                    <>
                                        <Typography variant="body2">{content}</Typography>
                                        {isProtected ? null : (
                                            <IconButton size="small">
                                                <SvgIcon component={IconCopy} />
                                            </IconButton>
                                        )}
                                    </>
                                )}
                            </ProtectedData>
                        </Box>
                    ) : null}
                </Collapse>
            ) : null}
        </div>
    );
};

export default React.forwardRef(ScorePane);
